/**
 * Aero Sites — shared helpers used by both the list page (AeroSites) and the
 * per-site detail page (AeroSiteDetail). Pure logic only: slug generation,
 * timestamp coercion, relative time, and the plain-language change summary.
 * Extracted so the master-detail split reuses one source of truth instead of
 * drifting copies.
 */
import { ADJECTIVES, NOUNS, RESERVED, DENY } from './slugWords';

export function generateRandomString(length) {
  // Lowercase-only: HTML sites serve on a {slug}.aeroapp.ai subdomain and DNS
  // lowercases the host, so the slug must be lowercase to match the serving
  // function's lowercased lookup (see api/site.js).
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

// Generate a slug that is not already used by any existing url doc (mirrors the
// collision check in FolioForm).
export async function generateUniqueSlug(db) {
  for (let attempt = 0; attempt < 5; attempt++) {
    const slug = generateRandomString(10);
    const snapshot = await db
      .collectionGroup('url')
      .where('generatedUrl', '==', slug)
      .limit(1)
      .get();
    if (snapshot.empty) return slug;
  }
  // Extremely unlikely; fall back to a longer string.
  return generateRandomString(14);
}

// A slug becomes the whole subdomain, so it must be a valid DNS label, short
// enough to read, free of operational/reserved labels, and free of unsafe
// substrings. Returns true only when all of those hold.
export function isValidReadableSlug(slug) {
  if (typeof slug !== 'string') return false;
  if (slug.length > 30) return false;
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(slug)) return false;
  if (slug.includes('--')) return false;
  if (RESERVED.has(slug)) return false;
  for (const bad of DENY) {
    if (slug.includes(bad)) return false;
  }
  return true;
}

// Generate a friendly, readable `${adjective}-${noun}` slug (e.g. swift-otter)
// that is not already used by any existing url doc. Uniqueness is checked the
// same way generateUniqueSlug does it: a collectionGroup('url') query on
// generatedUrl, treating an empty result as available.
export async function generateReadableSlug(db) {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  for (let attempt = 0; attempt < 8; attempt++) {
    const adj = pick(ADJECTIVES);
    const noun = pick(NOUNS);
    // First 3 attempts use the bare two-word form; from the 4th on, append a
    // 2-3 digit number to widen the space when the bare names keep colliding.
    const candidate =
      attempt < 3
        ? `${adj}-${noun}`
        : `${adj}-${noun}-${Math.floor(Math.random() * 990) + 10}`;

    if (!isValidReadableSlug(candidate)) continue;

    const snapshot = await db
      .collectionGroup('url')
      .where('generatedUrl', '==', candidate)
      .limit(1)
      .get();
    if (snapshot.empty) return candidate;
  }

  // All 8 attempts collided: fall back to a 4-digit suffix, validated and
  // checked once more.
  const candidate = `${pick(ADJECTIVES)}-${pick(NOUNS)}-${Math.floor(Math.random() * 9000) + 1000}`;
  if (isValidReadableSlug(candidate)) {
    const snapshot = await db
      .collectionGroup('url')
      .where('generatedUrl', '==', candidate)
      .limit(1)
      .get();
    if (snapshot.empty) return candidate;
  }
  return candidate;
}

// Copy a site's active version onto a freshly duplicated url doc. Reads the
// source version, writes a single new version doc on the duplicate (blobs are
// content-addressed and shared per user, so nothing re-uploads), then points
// the duplicate's activeVersion at it and mirrors homePage/serveMode onto the
// url doc the same way publish does. The change summary marks every path as
// added since the duplicate has no prior version. No-op when the source has
// no active version or the version doc is missing.
export async function copyActiveVersion(db, uid, sourceSiteId, sourceActiveVersion, targetSiteId) {
  if (!sourceActiveVersion) return null;
  const urlDoc = (id) =>
    db.collection('user').doc(uid).collection('url').doc(id);
  const versionSnap = await urlDoc(sourceSiteId)
    .collection('versions').doc(sourceActiveVersion)
    .get();
  if (!versionSnap.exists) return null;
  const v = versionSnap.data() || {};
  const files = v.files || {};
  const newVersion = {
    createdAt: new Date(),
    files,
    fileCount: v.fileCount || Object.keys(files).length,
    totalBytes: v.totalBytes || 0,
    changeSummary: { added: Object.keys(files), changed: [], removed: [] },
    homePage: v.homePage || 'index.html',
    serveMode: v.serveMode || 'multipage',
  };
  if (v.sizes) newVersion.sizes = v.sizes;
  const versionRef = urlDoc(targetSiteId).collection('versions').doc();
  await versionRef.set(newVersion);
  await urlDoc(targetSiteId).set(
    {
      type: 'html',
      activeVersion: versionRef.id,
      homePage: newVersion.homePage,
      serveMode: newVersion.serveMode,
      updatedAt: new Date(),
    },
    { merge: true }
  );
  return versionRef.id;
}

// Firestore Timestamp | Date | null -> Date | null.
export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

// Plain-language relative time (mirrors UTProjectRow).
export function relativeTime(value) {
  const date = toDate(value);
  if (!date) return 'Just now';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / (1000 * 60));
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
}

// relativeTime for use after other words ("Updated just now", "Updated
// yesterday"): sentence case, so the words it starts with are lowercased.
// Dates are left as they are.
export function relativeTimeInline(value) {
  const label = relativeTime(value);
  return /^(Just now|Yesterday)$/.test(label) ? label.toLowerCase() : label;
}

// Friendly absolute date for version rows and the version page title:
// "Today, 11:42 AM", "Yesterday, 4:12 PM", else "Jun 2, 9:30 AM" (the year is
// added only when it differs from the current year).
export function formatVersionDate(value) {
  const date = toDate(value);
  if (!date) return 'Just now';
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (dayDiff === 0) return `Today, ${time}`;
  if (dayDiff === 1) return `Yesterday, ${time}`;
  const opts = { month: 'short', day: 'numeric' };
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return `${date.toLocaleDateString(undefined, opts)}, ${time}`;
}

// Short human-readable byte size ("864 B", "12 KB", "1.2 MB"). Returns an
// empty string for missing input so callers can render nothing gracefully.
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 10 ? gb.toFixed(1) : Math.round(gb)} GB`;
}

// Turn a version's changeSummary into a human, jargon-free sentence:
//   "First published, 48 files" · "Replaced one file" · "You changed 2 files".
export function describeChange(version) {
  const cs = version.changeSummary || {};
  const added = (cs.added || []).length;
  const changed = (cs.changed || []).length;
  const removed = (cs.removed || []).length;
  const fileCount = version.fileCount || added + changed;
  const word = (n) => (n === 1 ? 'file' : 'files');

  // First publish: every file is new, nothing changed or removed.
  if (added > 0 && changed === 0 && removed === 0 && added === fileCount) {
    return `First published, ${added} ${word(added)}`;
  }
  if (added === 0 && changed === 0 && removed === 0) {
    return 'Published again, nothing changed';
  }
  // A single edited file reads more naturally as a replacement.
  if (changed === 1 && added === 0 && removed === 0) {
    return 'Replaced one file';
  }
  const parts = [];
  if (changed) parts.push(`changed ${changed} ${word(changed)}`);
  if (added) parts.push(`added ${added} ${word(added)}`);
  if (removed) parts.push(`removed ${removed} ${word(removed)}`);
  return `You ${parts.join(', ')}`;
}
